import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { keyframes, css } from 'styled-components'
import WidgetCard from './WidgetCard'
import { GhostButton, PrimaryButton } from '../../../layout/styledComponents'
import { parseUtcDate } from '../../../utils/dates'
import {
  getSessionProgressState,
  progressMarkerLabel,
} from '../../../utils/sessionProgress'

const liveDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50%      { transform: scale(1.2); opacity: 0.6; }
`

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const SessionRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 14px;
  background: ${(p) =>
    p.$progressState === 'IN_PROGRESS'
      ? p.theme?.color?.accentSoft || '#E1ECE7'
      : p.theme?.color?.bgRaised || '#fff'};
  border: 1px solid
    ${(p) =>
      p.$progressState === 'IN_PROGRESS'
        ? p.theme?.color?.accent || '#6B9080'
        : p.$progressState === 'RECENTLY_ENDED'
          ? p.theme?.color?.borderStrong || '#D5CCBC'
          : p.theme?.color?.accent || '#6B9080'};
  flex-wrap: wrap;
`

const Detail = styled.div`
  flex: 1;
  min-width: 0;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const Title = styled.span`
  font-weight: 600;
  font-size: 15px;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
`

const Sub = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  margin-top: 2px;
`

const Window = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${(p) => p.theme?.color?.accent || '#6B9080'};
  margin-top: 4px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`

const Marker = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${(p) =>
    p.$tone === 'success'
      ? p.theme?.color?.accent || '#6B9080'
      : p.$tone === 'muted'
        ? p.theme?.color?.bgSubtle || '#F4F0E9'
        : p.theme?.color?.primarySoft || '#E8EEF6'};
  color: ${(p) =>
    p.$tone === 'success'
      ? '#fff'
      : p.$tone === 'muted'
        ? p.theme?.color?.inkMuted || '#6B7280'
        : p.theme?.color?.primary || '#1F3A5F'};
  &::before {
    ${(p) =>
      p.$tone === 'success' &&
      css`
        content: '';
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #fff;
        animation: ${liveDot} 1.6s ease-in-out infinite;
      `}
  }
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

function describeWindow(scheduledAt, joinWindowEndsAt) {
  if (!scheduledAt) return null
  const start = parseUtcDate(scheduledAt).getTime()
  const now = Date.now()
  const minutesToStart = Math.round((start - now) / 60000)
  if (minutesToStart > 0) {
    if (minutesToStart === 1) return 'Starts in 1 min'
    return `Starts in ${minutesToStart} min`
  }
  if (joinWindowEndsAt) {
    const end = parseUtcDate(joinWindowEndsAt).getTime()
    const minutesToEnd = Math.round((end - now) / 60000)
    if (minutesToEnd <= 0) return null
    const t = parseUtcDate(joinWindowEndsAt).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    })
    return `Open until ${t}`
  }
  return null
}

/**
 * Lists every currently-joinable session for the active patient. Drives the
 * patient-portal "click here to join" surface on Today — a session shows up
 * here from {@code startTime - 30min} through {@code endTime + 180min}
 * (matches the {@code /v/*} JWT validity window so the in-app CTA window
 * doesn't lag the public link).
 *
 * <p>Source: union of {@code data.nextSession} and
 * {@code data.upcomingSessions} filtered by {@code canJoinNow === true}.
 * The peer NextSessionCard / UpcomingSessionsCard widgets are responsible
 * for skipping any session that LiveNowCard rendered to avoid duplication.
 * Each row is colour-coded by progress state — IN_PROGRESS gets a pulsing
 * sage border + filled "LIVE" pill, RECENTLY_ENDED gets a calmer border
 * with a "Just ended" pill so the patient knows the slot finished but the
 * link still works (recap conversations, reconnects).</p>
 */
export default function LiveNowCard({ nextSession, upcomingSessions }) {
  const navigate = useNavigate()
  const [, setTick] = useState(0)
  // Re-render every 30s so the "Starts in N min" / "open until 4:15 PM"
  // line stays fresh and the card auto-hides when the window expires.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000)
    return () => clearInterval(id)
  }, [])

  const joinable = []
  const seen = new Set()
  const collect = (s) => {
    if (!s || !s.canJoinNow || !s.eventId) return
    if (seen.has(s.eventId)) return
    seen.add(s.eventId)
    joinable.push(s)
  }
  collect(nextSession)
  ;(upcomingSessions || []).forEach(collect)

  if (joinable.length === 0) return null

  joinable.sort((a, b) => {
    const at = a.scheduledAt ? parseUtcDate(a.scheduledAt).getTime() : 0
    const bt = b.scheduledAt ? parseUtcDate(b.scheduledAt).getTime() : 0
    return at - bt
  })

  const anyInProgress = joinable.some((s) => getSessionProgressState(s) === 'IN_PROGRESS')

  return (
    <WidgetCard
      tone="success"
      title={anyInProgress ? 'Live now' : 'Ready to join'}
      pulse={anyInProgress}
      badge={joinable.length > 1 ? `${joinable.length} sessions` : undefined}
    >
      <List>
        {joinable.map((s) => {
          // Live-session resolution at /l/{slug} keys on the booked-event slug;
          // /l/{eventId} doesn't resolve at PublicVideoSessionController. Prefer
          // the server-built joinUrl, then s.slug, and only as a last-ditch
          // legacy fallback fall through to s.eventId.
          const url = s.joinUrl || (s.slug ? `/l/${s.slug}` : `/l/${s.eventId}`)
          const progress = getSessionProgressState(s)
          const markerLabel = progressMarkerLabel(progress)
          const markerTone =
            progress === 'IN_PROGRESS'
              ? 'success'
              : progress === 'RECENTLY_ENDED'
                ? 'muted'
                : 'primary'
          return (
            <SessionRow key={s.eventId} $progressState={progress}>
              <Detail>
                <TitleRow>
                  <Title>{s.title || 'Session'}</Title>
                  {markerLabel ? (
                    <Marker $tone={markerTone}>{markerLabel}</Marker>
                  ) : null}
                </TitleRow>
                <Sub>{s.practitionerName}</Sub>
                {describeWindow(s.scheduledAt, s.joinWindowEndsAt) ? (
                  <Window>{describeWindow(s.scheduledAt, s.joinWindowEndsAt)}</Window>
                ) : null}
              </Detail>
              <Actions>
                <PrimaryButton onClick={() => navigate(url)}>Join now</PrimaryButton>
                <GhostButton
                  onClick={() => navigate(`/patient-home/sessions/${s.eventId}`)}
                >
                  Details
                </GhostButton>
              </Actions>
            </SessionRow>
          )
        })}
      </List>
    </WidgetCard>
  )
}
